import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, IdCard, KeyRound } from "lucide-react";
import { rolesApi, rolesQueryKeys } from "../../shared/api/roles";
import { apiError } from "../../shared/api/client";
import { systemRoleLabel, systemRoleDescription } from "../../shared/format";
import { useOperatedModules } from "../../shared/hooks/useOperatesModule";
import { moduleOfPermission } from "../../shared/screens/permissionModule";
import { RoleMatrix } from "./components/RoleMatrix";
import { ProUpsellCard } from "./components/ProUpsellCard";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function RolesSettingsPage() {
  const roles = useQuery({
    queryKey: rolesQueryKeys.list(),
    queryFn: () => rolesApi.list(),
  });
  const permissions = useQuery({
    queryKey: rolesQueryKeys.permissions(),
    queryFn: () => rolesApi.permissions(),
  });
  // Ẩn các dòng quyền của vùng phòng khám không dùng — vì chế độ vận hành (ADR-0048) hay vì gói
  // (ADR-0053). Lạc quan khi chưa biết: thừa vài dòng trong chốc lát vô hại hơn là ma trận nháy.
  const modules = useOperatedModules();

  return (
    <div className="space-y-5 font-sans">
      <PageHeader
        title="Phân quyền hệ thống"
        description={
          <>
            Quản lý quyền hạn của từng nhân viên trong HIS. Mỗi tài khoản được gắn một
            hoặc nhiều <strong>vai trò hệ thống</strong> — xác định những chức năng nào
            họ có thể truy cập.
          </>
        }
        actions={
          <Button asChild variant="secondary">
            <Link to="/settings/users">
              <ArrowLeft className="h-4 w-4" />
              Người dùng
            </Link>
          </Button>
        }
      />

      {/* Concept callout */}
      <Card className="border-l-4 border-l-primary bg-muted/40">
        <CardContent className="pt-6">
          <div className="text-sm">
            <strong>Hai khái niệm khác nhau:</strong>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-4 text-[13px] sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1.5 font-semibold">
                <IdCard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Vị trí (HR)
              </div>
              <div className="text-muted-foreground">
                Chức danh nhân sự: <em>Bác sĩ, Điều dưỡng, Lễ tân…</em>
                <br />
                Dùng cho hồ sơ nhân viên, báo cáo, lịch làm việc. Không ảnh hưởng đến
                quyền hạn trong phần mềm.
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 font-semibold">
                <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Vai trò hệ thống (RBAC)
              </div>
              <div className="text-muted-foreground">
                Quyền hạn truy cập: <em>Kê đơn &amp; Khám bệnh, Tiếp đón &amp; Thu ngân…</em>
                <br />
                Quyết định màn hình nào, thao tác nào nhân viên được phép thực hiện.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ProUpsellCard title="Tạo vai trò tuỳ chỉnh — Gói Pro Enterprise">
        HIS Lite cung cấp các vai trò hệ thống cố định. Nếu bạn cần tạo vai trò riêng
        (VD: "Y tá trưởng", "Kế toán trưởng") với bộ quyền tuỳ ý, hãy nâng cấp Pro
        Enterprise để mở khoá tính năng tạo vai trò + tuỳ chỉnh quyền theo từng người.
      </ProUpsellCard>

      <Card>
        <CardHeader>
          <CardTitle>Vai trò trong hệ thống</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {roles.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
          {roles.isError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError(roles.error).detail}
            </div>
          )}
          {roles.data?.map((r) => {
            const label = systemRoleLabel[r.code] ?? r.name;
            const desc = systemRoleDescription[r.code] ?? r.description ?? "";
            return (
              <div
                key={r.id}
                className="flex flex-col gap-2 border-b py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium">
                    {label}
                    {r.isSystem && <Badge variant="muted">Hệ thống</Badge>}
                  </div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {desc && <span className="mr-2">{desc}</span>}
                    <span className="font-mono text-muted-foreground">{r.code}</span>
                  </div>
                </div>
                <div className="shrink-0 text-sm text-muted-foreground">
                  {r.permissionCodes.length} quyền
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Bảng ma trận quyền</CardTitle>
          <span className="text-xs text-muted-foreground">✓ = vai trò có quyền này</span>
        </CardHeader>
        <CardContent>
          {(roles.isLoading || permissions.isLoading) && (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          )}
          {(roles.isError || permissions.isError) && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError(roles.error ?? permissions.error).detail}
            </div>
          )}
          {roles.data && permissions.data && (
            <RoleMatrix
              roles={roles.data}
              permissions={permissions.data.filter((p) => {
                const module = moduleOfPermission(p.code);
                return !module || modules.operates(module);
              })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
